import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'ramda';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { codes as paymentCodes } from 'constants/payment';
import Button from 'components/ui/Button';
import { Row } from 'components/shared/Layout';
import { compensationsShape, paymentsShape } from 'constants/propTypes';
import { getBusinessAccountPaymentsWithAmounts } from 'components/ui/Payments/Payments.utils';
import { BusinessAccountButton } from 'components/ui/Payments/SplitRefundMethods/BusinessAccountRefund/BusinessAccountRefund';
import { REFUND_25, REFUND_50 } from 'constants/prrRefund';

import { getTotalPrice, getRefundAmount, getRefundOptions } from './PrrRefund.utils';
import RefundAmountSelection from './RefundAmountSelection';
import { Error } from './PrrRefund.styles';
import CompensationBreakdown from './CompensationBreakdown/CompensationBreakdown';

const PrrRefundBAF = ({
  onSubmit,
  reference,
  filteredProducts,
  compensateLegs,
  compensationItems = [],
  payments,
  loading,
  itemRefs,
  currency
}) => {
  const refundOptions = getRefundOptions(compensationItems, undefined, [REFUND_25, REFUND_50]);

  const {
    register,
    handleSubmit,
    clearErrors,
    formState: { isValid, errors },
    watch
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      refundPercentage: String(refundOptions?.[0]?.value ?? REFUND_25.value),
      account: undefined
    }
  });

  const refundPercentage = watch('refundPercentage');
  const account = watch('account');

  const businessAccounts = getBusinessAccountPaymentsWithAmounts(payments);

  const selectedAccountAvailable = useMemo(() => {
    if (!account) return null;
    const selected = businessAccounts.find((acc) => acc.accountNumber === account);
    return selected?.availableRefundAmount ?? null;
  }, [account, businessAccounts]);

  const { refundAmount } = useMemo(() => {
    let totalPrice = 0;
    let refundAmount = 0;

    if (compensationItems.length > 0) {
      compensationItems.forEach(({ ticketPrice, cashCompensationAmount }) => {
        totalPrice += ticketPrice;
        refundAmount += cashCompensationAmount;
      });
    } else {
      totalPrice = getTotalPrice(filteredProducts);
      refundAmount = getRefundAmount(totalPrice, refundPercentage);
    }

    return {
      totalPrice,
      refundAmount
    };
  }, [compensationItems, filteredProducts, refundPercentage]);

  const onSubmitForm = () => {
    const variables = {
      currency: compensationItems?.[0]?.currency ?? currency,
      bookingReference: reference,
      account,
      refundAmount: Number(refundAmount),
      additionalDetails: itemRefs.map((ref) => {
        const product = filteredProducts.find(({ itemRef }) => itemRef === ref);

        const compensationRecord = compensationItems.find(({ ticketNumber }) => product?.ticketNumber === ticketNumber);

        return {
          itemRefId: Number(ref),
          key: `PRR_Compensation_${refundPercentage}%`,
          value: compensationRecord
            ? String(compensationRecord.cashCompensationAmount)
            : String(getRefundAmount(product.price, refundPercentage))
        };
      }),
      ...(compensationItems.length > 0 && {
        compensationRecordsToUpdate: compensationItems.map(({ compensationRecordId, caseId }) => ({
          compensationRecordId,
          caseId
        }))
      })
    };
    onSubmit({ method: paymentCodes.BUSINESS_ACCOUNT, variables });
  };

  const hasAccountError = Boolean(errors?.account);

  return (
    <div>
      <h2>
        <FormattedMessage id="prrRefund.compensationAmount" />
      </h2>

      <Row>
        <form onSubmit={handleSubmit(onSubmitForm)}>
          <Row>
            <RefundAmountSelection options={refundOptions} register={register} />
          </Row>

          <h2>
            <FormattedMessage id="prrRefund.compensationTickets" />
          </h2>
          <CompensationBreakdown
            currency={currency}
            compensateLegs={compensateLegs}
            refundPercentage={refundPercentage}
            compensationItems={compensationItems}
          />

          <Row>
            <h2>
              <FormattedMessage id="prrRefund.processRefund" />
            </h2>
          </Row>

          <Row>
            <h3>
              <FormattedMessage id="prrRefund.selectAccount" />
            </h3>
            <Row data-cy="baf-list" span={6}>
              {businessAccounts.map(({ accountNumber, originalAmount, availableRefundAmount }) => (
                <BusinessAccountButton
                  key={accountNumber}
                  accountNumber={accountNumber}
                  originalAmount={originalAmount}
                  availableRefundAmount={availableRefundAmount}
                  currency={currency}
                  register={register}
                  clearErrors={clearErrors}
                />
              ))}
            </Row>
          </Row>

          {hasAccountError && (
            <Error data-cy="account-error-msg">
              <FormattedMessage id="refunds.businessAccount.error.account" />
            </Error>
          )}

          <Button
            data-cy="submit-prr-refund"
            type="submit"
            disabled={
              !isValid || loading || isEmpty(payments) || (selectedAccountAvailable !== null && refundAmount > selectedAccountAvailable)
            }
            loading={loading}
          >
            <FormattedMessage id="prrRefund.processRefundCta" />
          </Button>
        </form>
      </Row>
    </div>
  );
};

PrrRefundBAF.propTypes = {
  filteredProducts: PropTypes.arrayOf(
    PropTypes.shape({
      itemRef: PropTypes.string,
      passengerType: PropTypes.string,
      bucketCode: PropTypes.string,
      productFamily: PropTypes.shape({
        code: PropTypes.string
      }),
      currency: PropTypes.string,
      loyaltyPoints: PropTypes.number,
      price: PropTypes.number
    })
  ),
  reference: PropTypes.string,
  onSubmit: PropTypes.func,
  payments: paymentsShape.isRequired,
  loading: PropTypes.bool,
  itemRefs: PropTypes.arrayOf(PropTypes.string),
  compensateLegs: PropTypes.arrayOf(
    PropTypes.shape({
      legName: PropTypes.string,
      products: PropTypes.arrayOf(
        PropTypes.shape({
          itemRef: PropTypes.string,
          passengerType: PropTypes.string,
          bucketCode: PropTypes.string,
          productFamily: PropTypes.shape({
            code: PropTypes.string
          }),
          currency: PropTypes.string,
          loyaltyPoints: PropTypes.number,
          price: PropTypes.number
        })
      )
    })
  ),
  compensationItems: compensationsShape,
  currency: PropTypes.string
};

export default PrrRefundBAF;
